import { z } from "zod";
import { jsonObjectSchema } from "./common.contract.ts";

const boundedJsonSchema = z
    .json()
    .refine(
        (value) =>
            new TextEncoder().encode(JSON.stringify(value)).byteLength <=
            1_000_000,
        { message: "JSON value exceeds one megabyte" },
    );

export const MAX_PUBLIC_TOOL_DATA_BYTES = 600_000;

export const MAX_TOOL_RESULT_BYTES = 5_000_000;
export const publicToolDataSchema = boundedJsonSchema.refine(
    (value) =>
        new TextEncoder().encode(JSON.stringify(value)).byteLength <=
        MAX_PUBLIC_TOOL_DATA_BYTES,
    { message: "Public tool data exceeds the public byte limit" },
);

export const agentToolContentSchema = z.discriminatedUnion("type", [
    z
        .object({ type: z.literal("text"), text: z.string().max(131_072) })
        .strict(),
    z
        .object({
            type: z.literal("image"),
            data: z.base64().max(4_000_000),
            mimeType: z.string().min(1).max(255),
        })
        .strict(),
]);

export const agentToolResultMetadataSchema = z
    .object({
        publicData: publicToolDataSchema.optional(),
        authoredToolResults: z
            .array(
                z
                    .object({
                        toolId: z.string().min(1).max(256),
                        toolCallId: z.string().min(1).max(512),
                        input: jsonObjectSchema,
                        output: boundedJsonSchema.optional(),
                        publicData: publicToolDataSchema.optional(),
                    })
                    .strict(),
            )
            .max(100)
            .refine(
                (value) =>
                    new TextEncoder().encode(JSON.stringify(value))
                        .byteLength <= MAX_PUBLIC_TOOL_DATA_BYTES,
            )
            .optional(),
    })
    .strict();

export const authoredToolResultsSchema =
    agentToolResultMetadataSchema.shape.authoredToolResults.unwrap();

export const agentToolResultSchema = z
    .object({
        content: z.array(agentToolContentSchema).max(100),
        structured: boundedJsonSchema.optional(),
        metadata: agentToolResultMetadataSchema.optional(),
        isError: z.boolean().optional(),
    })
    .strict()
    .refine(
        (result) =>
            new TextEncoder().encode(JSON.stringify(result)).byteLength <=
            MAX_TOOL_RESULT_BYTES,
        { message: "Tool result exceeds the five-megabyte absolute limit" },
    );

export type AgentToolContent = z.infer<typeof agentToolContentSchema>;
export type AgentToolResult = z.infer<typeof agentToolResultSchema>;
